import { useEffect, useRef, useState } from 'react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Button,
  Container,
  Link,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../shared/hooks/useAuth';
import { useApiError } from '../../shared/errors/useApiError';
import { useNotification } from '../../shared/notifications/NotificationProvider';
import { useSelectedCountry } from '../../shared/hooks/useSelectedCountry';
import {
  DEFAULT_REQUEST_DURATION,
  REQUEST_DURATIONS,
  type CreateRequestPayload,
  type RequestDurationDays,
} from '../../shared/services/requests.service';
import { useRequestMutations } from './useRequests';

/** sessionStorage key used to survive a redirect through the signup flow.
 *  When the visitor clicks "Publish" without an account, the form payload
 *  is stashed here and replayed automatically once they come back signed-in. */
const DRAFT_KEY = 'community-request-draft';

export function CreateRequestPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { isAuthenticated } = useAuth();
  const { notify } = useNotification();
  const { translate } = useApiError();
  const { countryCode } = useSelectedCountry();
  const { create } = useRequestMutations();

  // The "no result" CTA in /search forwards the original query as ?title=…
  // so the new-request form lands pre-filled with what the visitor was looking for.
  const [title, setTitle] = useState(searchParams.get('title') ?? '');
  const [cityName, setCityName] = useState(searchParams.get('cityName') ?? '');
  const [description, setDescription] = useState('');
  const [duration, setDuration] = useState<RequestDurationDays>(DEFAULT_REQUEST_DURATION);
  const [touched, setTouched] = useState(false);

  const titleValid = title.trim().length > 0;
  const canSubmit = titleValid && !create.isPending;

  const buildPayload = (): CreateRequestPayload => ({
    title: title.trim(),
    description: description.trim() || undefined,
    cityName: cityName.trim() || undefined,
    countryCode: (countryCode || '').toUpperCase() || undefined,
    durationDays: duration,
  });

  const publish = async (payload: CreateRequestPayload) => {
    try {
      const r = await create.mutateAsync(payload);
      notify.success(t('requests.createdNotice'));
      navigate(`/requests/${r.id}`, { replace: true });
    } catch (err) {
      notify.error(translate(err));
    }
  };

  // Replay a draft that was stashed before the signup detour. Guarded by a
  // ref so React's StrictMode double-invoke doesn't fire two publishes.
  const replayedRef = useRef(false);
  useEffect(() => {
    if (!isAuthenticated || replayedRef.current) return;
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return;
    replayedRef.current = true;
    sessionStorage.removeItem(DRAFT_KEY);
    try {
      const draft = JSON.parse(raw) as CreateRequestPayload;
      // Reflect the draft in the UI in case publish fails — the user can edit
      // and retry without losing what they typed.
      setTitle(draft.title ?? '');
      setCityName(draft.cityName ?? '');
      setDescription(draft.description ?? '');
      if (draft.durationDays) setDuration(draft.durationDays);
      publish(draft);
    } catch {
      // Corrupted draft — drop it silently, the user can retype.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!canSubmit) return;
    const payload = buildPayload();
    if (!isAuthenticated) {
      // No account yet — stash the draft, prompt to register, then bounce
      // back here. The useEffect above auto-publishes once signed in.
      sessionStorage.setItem(DRAFT_KEY, JSON.stringify(payload));
      notify.info(t('requests.signupRequiredNotice'));
      navigate('/register?next=/requests/new');
      return;
    }
    await publish(payload);
  };

  return (
    <Container maxWidth="sm" sx={{ py: { xs: 3, md: 5 } }}>
      <Link component={RouterLink} to="/requests" sx={{ display: 'inline-block', mb: 2 }}>
        ← {t('requests.backToList')}
      </Link>
      <Typography variant="h3" sx={{ mb: 0.5 }}>
        {t('requests.createTitle')}
      </Typography>
      <Typography color="text.secondary" sx={{ mb: 3 }}>
        {t('requests.createSubtitle')}
      </Typography>

      <Paper variant="outlined" sx={{ p: { xs: 2.5, md: 3.5 }, borderRadius: 3 }}>
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2.5}>
            <TextField
              label={t('requests.fieldTitle')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              fullWidth
              autoFocus
              error={touched && !titleValid}
              helperText={
                touched && !titleValid
                  ? t('requests.errorRequired')
                  : t('requests.fieldTitleHelper')
              }
              inputProps={{ maxLength: 160 }}
            />

            <TextField
              label={t('requests.fieldCity')}
              value={cityName}
              onChange={(e) => setCityName(e.target.value)}
              fullWidth
              placeholder={t('requests.fieldCityPlaceholder')}
              inputProps={{ maxLength: 160 }}
            />

            <TextField
              label={t('requests.fieldDescription')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              fullWidth
              multiline
              minRows={4}
              placeholder={t('requests.fieldDescriptionPlaceholder')}
              inputProps={{ maxLength: 2000 }}
              helperText={t('requests.fieldDescriptionHelper', {
                count: description.trim().length,
              })}
            />

            <TextField
              select
              label={t('requests.fieldDuration')}
              value={duration}
              onChange={(e) => setDuration(Number(e.target.value) as RequestDurationDays)}
              fullWidth
            >
              {REQUEST_DURATIONS.map((d) => (
                <MenuItem key={d} value={d}>
                  {t('requests.durationLabel', { count: d })}
                </MenuItem>
              ))}
            </TextField>

            <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
              <Button component={RouterLink} to="/requests" variant="outlined">
                {t('common.cancel')}
              </Button>
              <Box sx={{ flex: 1 }} />
              <Button type="submit" variant="contained" disabled={!canSubmit}>
                {create.isPending ? t('common.loading') : t('requests.publishAction')}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Paper>
    </Container>
  );
}
